import { useState } from 'react'
import { Dialog, DialogBackdrop, DialogTitle } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import {
  MonthlyRepeatRule,
  RepeatSchedule,
} from '../../lib/shared/calendar.types'
import { getRepeatDescription } from './RepeatDescription'
import { WeeklyRecurrence } from './recurrence-types/WeeklyRecurrence'
import { MonthlyRecurrence } from './recurrence-types/MonthlyRecurrence'
import { AnnualRecurrence } from './recurrence-types/AnnualRecurrence'
import { QuarterlyRecurrence } from './recurrence-types/QuarterlyRecurrence'
import { createRecurringEvent } from '../../lib/db'
import { toast } from 'sonner'

interface CreateRepeatingEventFormProps {
  workspaceId: string
  accountingReferenceDate?: string
  onClose: () => void
}

type InitialRule =
  | {
      frequency: 'weekly'
      daysOfWeek: number[]
    }
  | {
      frequency: 'monthly'
      type: 'dayOfMonth'
      value: number
    }
  | {
      frequency: 'daily'
    }
  | {
      frequency: 'quarterly'
      type: 'dayOfMonth'
      value: number
    }
  | {
      frequency: 'annually'
      type: 'dayOfMonth'
      value: number
      month: number
    }

export function CreateRepeatingEventForm({
  workspaceId,
  accountingReferenceDate,
  onClose,
}: CreateRepeatingEventFormProps) {
  const [name, setName] = useState('')
  const [schedule, setSchedule] = useState<RepeatSchedule>({
    rule: {
      frequency: 'monthly',
      type: 'dayOfMonth',
      value: new Date().getDate(),
    } as MonthlyRepeatRule,
    start: new Date().toISOString().split('T')[0],
  })

  const handleFrequencyChange = (
    frequency: RepeatSchedule['rule']['frequency'],
  ) => {
    let newRule: InitialRule

    switch (frequency) {
      case 'weekly':
        newRule = {
          frequency: 'weekly',
          daysOfWeek: [new Date().getDay()],
        }
        break
      case 'monthly':
        newRule = {
          frequency: 'monthly',
          type: 'dayOfMonth',
          value: new Date().getDate(),
        }
        break
      case 'daily':
        newRule = {
          frequency: 'daily',
        }
        break
      case 'quarterly':
        newRule = {
          frequency: 'quarterly',
          type: 'dayOfMonth',
          value: new Date().getDate(),
        }
        break
      case 'annually':
        newRule = {
          frequency: 'annually',
          type: 'dayOfMonth',
          value: new Date().getDate(),
          month: new Date().getMonth() + 1,
        }
        break
    }

    setSchedule({
      ...schedule,
      rule: newRule as RepeatSchedule['rule'],
    })
  }

  const isFormValid = () => {
    if (!name.trim()) return false

    if (!schedule.start) return false

    if (schedule.end && schedule.end <= schedule.start) return false

    if (
      schedule.rule.frequency === 'monthly' &&
      schedule.rule.type === 'dayOfMonth'
    ) {
      const startDate = new Date(schedule.start)
      const selectedDay = schedule.rule.value
      const actualDay = startDate.getDate()

      const daysInMonth = new Date(
        startDate.getFullYear(),
        startDate.getMonth() + 1,
        0,
      ).getDate()

      if (actualDay !== Math.min(selectedDay, daysInMonth)) {
        return false
      }
    }

    if (
      schedule.rule.frequency === 'weekly' &&
      (!Array.isArray(schedule.rule.daysOfWeek) ||
        schedule.rule.daysOfWeek.length === 0)
    ) {
      return false
    }

    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await createRecurringEvent({
      workspaceId,
      event: {
        name,
        repeats: schedule,
      },
    })
    onClose()
    toast.success('Recurring event created')
  }

  return (
    <Dialog
      open={true}
      onClose={onClose}
      className="fixed inset-0 z-10 overflow-y-auto"
    >
      <div className="flex min-h-screen items-center justify-center">
        <DialogBackdrop className="fixed inset-0 bg-black opacity-30" />

        <div className="relative bg-white rounded-lg max-w-2xl w-full mx-4 p-6">
          <div className="absolute top-4 right-4">
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <DialogTitle className="text-lg font-medium mb-4">
            Create recurring event
          </DialogTitle>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Event Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-grey-300 p-2 focus:border-blue-500 focus:ring-blue-500"
                  placeholder="e.g. Monthly Payroll"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Repeat Frequency
                </label>
                <div className="inline-flex rounded-md">
                  {[
                    { value: 'daily', label: 'Daily' },
                    { value: 'weekly', label: 'Weekly' },
                    { value: 'monthly', label: 'Monthly' },
                    { value: 'quarterly', label: 'Quarterly' },
                    { value: 'annually', label: 'Annually' },
                  ].map((option, idx) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() =>
                        handleFrequencyChange(
                          option.value as RepeatSchedule['rule']['frequency'],
                        )
                      }
                      className={`
                        px-4 py-2 text-sm font-medium
                        ${idx === 0 ? 'rounded-l-md' : ''}
                        ${idx === 4 ? 'rounded-r-md' : ''}
                        ${
                          schedule.rule.frequency === option.value
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }
                        border border-gray-300
                        focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500
                        ${idx !== 0 ? '-ml-px' : ''}
                      `}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {schedule.rule.frequency === 'weekly' && (
                <WeeklyRecurrence
                  rule={schedule.rule}
                  onChange={(newRule) =>
                    setSchedule({ ...schedule, rule: newRule })
                  }
                />
              )}

              {schedule.rule.frequency === 'monthly' && (
                <MonthlyRecurrence
                  rule={schedule.rule}
                  onChange={(newRule) =>
                    setSchedule({ ...schedule, rule: newRule })
                  }
                />
              )}

              {schedule.rule.frequency === 'annually' && (
                <AnnualRecurrence
                  rule={schedule.rule}
                  onChange={(newRule) =>
                    setSchedule({ ...schedule, rule: newRule })
                  }
                />
              )}

              {schedule.rule.frequency === 'quarterly' && (
                <QuarterlyRecurrence
                  accountingReferenceDate={accountingReferenceDate}
                  rule={schedule.rule}
                  onChange={(newRule) =>
                    setSchedule({ ...schedule, rule: newRule })
                  }
                />
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={schedule.start}
                    onChange={(e) =>
                      setSchedule({
                        ...schedule,
                        start: e.target.value,
                      })
                    }
                    className="mt-1 block w-full rounded-md border border-grey-500 p-2 focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    End Date{' '}
                    <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <input
                    type="date"
                    value={schedule.end || ''}
                    onChange={(e) =>
                      setSchedule({
                        ...schedule,
                        end: e.target.value || undefined,
                      })
                    }
                    className="mt-1 block w-full rounded-md border border-grey-500 p-2 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="text-sm text-gray-500">
                {getRepeatDescription(schedule)}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isFormValid()}
                className={`px-4 py-2 rounded-md ${
                  isFormValid()
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-blue-300 text-white cursor-not-allowed'
                }`}
              >
                Create Event
              </button>
            </div>
          </form>
        </div>
      </div>
    </Dialog>
  )
}
